import type { Cart, OrderStatus, Quantity, UnitCode } from '~/graphql/graphql';
import type { OrderForBuilder } from '~/api/order';
import { getPlatformProductDimensions } from '~/utils/order';
import { quantityInUnit } from '~/utils/targetOrderSize';
import type { Dimensions } from '~/utils/unitConversion';

const PLACED_SUB_ORDER_STATUSES: OrderStatus[] = [
  'PENDING_SELLER_CONFIRMATION',
  'CONFIRMED',
  'SHIPPED',
  'DELIVERED',
];

// Each converted part and the required quantity round to 3dp, so an exactly filled line can total 0.0005 short per rounding.
const ROUNDING_ERROR = 0.0005;

export type SourcingCoverageRow = {
  platformProductsLengthId: string;
  platformProductId: string;
  productName: string;
  lengthDisplayValue: string;
  required: number;
  inCartQuantity: number;
  placedQuantity: number;
  covered: number;
  remaining: number;
  isCovered: boolean;
  unit: UnitCode;
};

export function computeSourcingCoverage(
  order: Pick<OrderForBuilder, 'items' | 'subOrders'>,
  cartsAtDropShipLocation: Cart[]
): SourcingCoverageRow[] {
  return order.items.map((item) => {
    const platformProductsLengthId = item.platformProductsLengths.id;

    const { platformProduct, length, isNominal } = item.platformProductsLengths;
    const dims = getPlatformProductDimensions(
      platformProduct,
      length,
      isNominal
    );
    // Unconvertible quantities count as 0 so a row never overstates coverage.
    const inLineUnit = (sum: number, quantity: Quantity) =>
      sum + (quantityInUnit(quantity, item.quantity.unit, dims) ?? 0);

    const placedParts = order.subOrders
      .filter((subOrder) => PLACED_SUB_ORDER_STATUSES.includes(subOrder.status))
      .flatMap((subOrder) => subOrder.items)
      .filter(
        (subItem) =>
          subItem.platformProductsLengths.id === platformProductsLengthId
      );
    const placedQuantity = placedParts.reduce(
      (sum, subItem) => inLineUnit(sum, subItem.quantity),
      0
    );

    const cartParts = cartsAtDropShipLocation
      .flatMap((cart) => cart.items ?? [])
      .filter(
        (cartItem) =>
          cartItem.platformProductsLengths?.id === platformProductsLengthId
      );
    const inCartQuantity = cartParts.reduce(
      (sum, cartItem) => inLineUnit(sum, cartItem.quantity),
      0
    );

    const covered = inCartQuantity + placedQuantity;
    const required = item.quantity.value;
    const tolerance =
      ROUNDING_ERROR * (placedParts.length + cartParts.length + 1);
    const isCovered = covered >= required - tolerance;

    return {
      platformProductsLengthId,
      platformProductId: item.platformProductsLengths.platformProduct.id,
      productName: item.platformProductsLengths.platformProduct.name,
      lengthDisplayValue: item.platformProductsLengths.lengthDisplayValue,
      required,
      inCartQuantity,
      placedQuantity,
      covered,
      remaining: isCovered ? 0 : Math.max(0, required - covered),
      isCovered,
      unit: item.quantity.unit,
    };
  });
}

export function remainingInUnit(
  row: SourcingCoverageRow,
  unit: UnitCode,
  dims: Dimensions
): number | null {
  return quantityInUnit({ value: row.remaining, unit: row.unit }, unit, dims);
}

export function cartsBelongingToParentOrder(
  carts: Cart[],
  parentOrderId: string
): Cart[] {
  return carts.filter((cart) => cart.parentOrder?.id === parentOrderId);
}

export function regularCarts(carts: Cart[]): Cart[] {
  return carts.filter((cart) => !cart.parentOrder);
}
